import _extends from "@babel/runtime/helpers/extends";
import path from 'path';
import { Prompt } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';
import { editPermissionSelector, viewPermissionSelector } from '../../common/classes/permissions';
import entityComposition from '../../common/classes/entityComposition';
import SectionPanel from '../../common/containers/sectionPanel';
import { hasAdminSubSectionPermission } from '../../../utils/paths';
import { isValid } from '../../../utils/passwordRules';
import EmployeeCertifications from './employeeCertifications';
import EmployeeContainer from './employeeContainer';
import EmployeeCredentials from './employeeCredentials';
import EmployeeGeneral from './employeeGeneral';
import EmployeeAssets from './employeeAssets';
import EmployeeJobs from './employeeJobs';
import routeHelper from '../../common/classes/routeHelper';
class Employee extends React.Component {
  constructor(props) {
    const match = props.match,
      change = props.employeeActions.change,
      active = props.employee.active;
    if (!match.params || !match.params.id) {
      throw new Error('props.match.params.id is required');
    }
    super(props);
    this.handleChange = (contentPath, value) => change(active.id, contentPath, value);
    this.handleSave = this.handleSave.bind(this);
    this.handleBack = routeHelper.updateURL.bind(routeHelper, '/admin/employees');
  }
  componentDidMount() {
    const _this$props = this.props,
      isNew = _this$props.employee.active.isNew,
      employeeActions = _this$props.employeeActions,
      match = _this$props.match,
      canView = _this$props.canView,
      location = _this$props.location;
    routeHelper.handleViewRedirect(canView, location);
    if (!isNew) {
      employeeActions.serverShow(match.params.id);
    }
  }
  componentWillUnmount() {
    const showCancel = this.props.employeeActions.showCancel;
    showCancel();
  }
  handleSave() {
    const _this$props2 = this.props,
      active = _this$props2.employee.active,
      employeeActions = _this$props2.employeeActions;
    const errors = this.validate();
    if (errors.length > 0) {
      employeeActions.errorMessage(errors);
    } else {
      employeeActions.serverSave(active);
    }
  }
  validate() {
    const active = this.props.employee.active;
    const errors = [];
    const assets = active.assets,
      password = active.password,
      lastName = active.lastName,
      firstName = active.firstName,
      certifications = active.certifications,
      hasPassword = active.hasPassword,
      username = active.username,
      groupAccessID = active.groupAccessID,
      email = active.email;
    if (!firstName) {
      errors.push('The user\'s first name is required.');
    }
    if (!lastName) {
      errors.push('The user\'s last name is required.');
    }
    if (password && password.length > 0 && !isValid(password)) {
      errors.push('The user\'s password does not meet all the requirements.');
    }
    if (assets && assets.length) {
      const noAsset = assets.filter(asset => typeof asset.assetID === 'undefined' || asset.assetID === null);
      if (noAsset.length) {
        errors.push('All of the user\'s associated assets require an asset to be selected.');
      }
    }
    if (certifications) {
      certifications.map(certification => {
        if (!certification.userCertificationTypeID) {
          errors.push('Please choose a certification type.');
        }
        return null;
      });
    }
    if (username && (hasPassword || password)) {
      if (!email) {
        errors.push('The user\'s email address is required.');
      }
      if (!groupAccessID) {
        errors.push('The user\'s user type is required.');
      }
    }
    return errors;
  }
  render() {
    const _this$props3 = this.props,
      _this$props3$employee = _this$props3.employee,
      pendingChanges = _this$props3$employee.pendingChanges,
      loading = _this$props3$employee.loading,
      active = _this$props3$employee.active,
      match = _this$props3.match,
      canEdit = _this$props3.canEdit;
    let section = match.params.section || 'general';
    const id = active.id,
      firstName = active.firstName,
      lastName = active.lastName,
      certifications = active.certifications,
      assets = active.assets,
      jobs = active.jobs,
      mfaEnabled = active.mfaEnabled,
      username = active.username,
      password = active.password,
      requireMFA = active.requireMFA;
    if (!hasAdminSubSectionPermission('employees_info')) {
      // we don't want to show the default section.
      section = '';
    }
    const props = {
      onChange: this.handleChange,
      userID: match.params.id
    };
    const forms = {
      '': /*#__PURE__*/React.createElement("div", null, "You do not have permissions to administer this information."),
      general: /*#__PURE__*/React.createElement(EmployeeGeneral, _extends({}, props, {
        active: active
      })),
      credentials: /*#__PURE__*/React.createElement(EmployeeCredentials, _extends({}, props, {
        id: id,
        mfaEnabled: mfaEnabled,
        requireMFA: requireMFA,
        username: username,
        password: password
      })),
      jobs: /*#__PURE__*/React.createElement(EmployeeJobs, _extends({}, props, {
        jobs: jobs
      })),
      certifications: /*#__PURE__*/React.createElement(EmployeeCertifications, _extends({}, props, {
        certifications: certifications
      })),
      assets: /*#__PURE__*/React.createElement(EmployeeAssets, _extends({}, props, {
        assets: assets
      }))
    };
    const sectionRoutes = Object.keys(forms).map(formKey => path.join('/admin/employees/', match.params.id, formKey));
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Prompt, {
      when: pendingChanges,
      message: location => routeHelper.transitionAsync(this, 'employee', location, sectionRoutes)
    }), /*#__PURE__*/React.createElement(EmployeeContainer, {
      section: section,
      id: match.params.id
    }, /*#__PURE__*/React.createElement(SectionPanel, {
      isPending: pendingChanges,
      isSaveDisabled: loading || !pendingChanges,
      showSave: canEdit,
      onBack: this.handleBack,
      onSave: this.handleSave,
      name: "".concat(firstName || '', " ").concat(lastName || '')
    }, forms[section])));
  }
}
Employee.defaultProps = {
  employee: {
    active: {
      mfaEnabled: false,
      assets: []
    }
  },
  loading: undefined,
  pendingChanges: false
};
Employee.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      section: PropTypes.string
    }).isRequired
  }).isRequired,
  employeeActions: PropTypes.shape({
    add: PropTypes.func,
    change: PropTypes.func,
    errorMessage: PropTypes.func,
    serverSave: PropTypes.func,
    serverShow: PropTypes.func,
    showCancel: PropTypes.func
  }).isRequired,
  employee: PropTypes.shape({
    active: PropTypes.shape({
      email: PropTypes.string,
      groupAccessID: PropTypes.string,
      password: PropTypes.string,
      lastName: PropTypes.string,
      firstName: PropTypes.string,
      username: PropTypes.string,
      hasPassword: PropTypes.bool,
      requireMFA: PropTypes.bool,
      mfaEnabled: PropTypes.bool,
      isNew: PropTypes.bool,
      id: PropTypes.string,
      assets: PropTypes.arrayOf(PropTypes.shape({})),
      userNumber: PropTypes.string,
      documents: PropTypes.arrayOf(PropTypes.shape({})),
      jobs: PropTypes.arrayOf(PropTypes.shape({})),
      certifications: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    loading: PropTypes.bool.isRequired,
    pendingChanges: PropTypes.bool.isRequired
  }),
  canEdit: PropTypes.bool.isRequired,
  canView: PropTypes.bool.isRequired,
  userID: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  pendingChanges: PropTypes.bool,
  location: PropTypes.shape({}).isRequired
};
export { Employee as TestEmployee };
export default entityComposition(Employee, ['employee'], {
  canView: state => viewPermissionSelector('employee')(state),
  canEdit: state => editPermissionSelector('employee')(state),
  userID: state => state.login.user.user_id
});